<template>
  <div class="system-diagram">
    <!-- 背景图和内容 -->
    <div class="diagram-content">
      <!-- 顶部渐变遮罩和标题 -->
      <div class="diagram-header">
        <div class="diagram-title">运行示意图</div>
        <img :src="require('@/assets/images/station-monitor/image_12.png')" class="fullscreen-icon"
          @click="openFullscreen" />
      </div>

      <SystemTopology :has-pv-module="props.hasPvModule" :has-charger-module="props.hasChargerModule"
        :diagram-data="props.diagramData" />
    </div>

    <!-- 全屏对话框 -->
    <v-dialog v-model="isFullscreen" :width="dialogWidth" :height="dialogHeight" persistent class="fullscreen-dialog">
      <v-card class="fullscreen-card">
        <!-- 对话框标题栏 -->
        <div class="fullscreen-header">
          <div class="fullscreen-title">运行示意图</div>
          <v-icon icon="mdi-close" class="close-icon" @click="closeFullscreen"></v-icon>
        </div>

        <!-- 放大的内容区域 -->
        <div class="fullscreen-body">
          <div class="fullscreen-content-wrapper" :style="{ transform: `scale(${scaleRatio})` }">
            <div class="fullscreen-content">
              <SystemTopology :has-pv-module="props.hasPvModule" :has-charger-module="props.hasChargerModule"
                :diagram-data="props.diagramData" />
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import SystemTopology from '../SystemTopology.vue'

const props = defineProps({
  hasPvModule: {
    type: Boolean,
    default: true,
  },
  hasChargerModule: {
    type: Boolean,
    default: true,
  },
  diagramData: {
    type: Object,
    default: () => ({})
  }
})

// 全屏状态
const isFullscreen = ref(false)

// 原始卡片尺寸
const ORIGINAL_WIDTH = 900
const ORIGINAL_HEIGHT = 577

// 窗口尺寸
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

const updateWindowSize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowSize)
    updateWindowSize()
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowSize)
  }
})

// 计算对话框宽度
const dialogWidth = computed(() => {
  // 限制最大宽度，但保证有边距
  const maxWidth = Math.min(windowWidth.value * 0.9, 1400)
  return Math.floor(maxWidth)
})

// 计算对话框高度
const dialogHeight = computed(() => {
  // 限制最大高度，保证一屏能放下
  const maxHeight = Math.min(windowHeight.value * 0.9, 900)
  return Math.floor(maxHeight)
})

// 计算缩放比例
const scaleRatio = computed(() => {
  const headerHeight = 60 // 标题栏高度
  const padding = 40 // 内容区域的内边距缓冲

  const availableWidth = dialogWidth.value - padding
  const availableHeight = dialogHeight.value - headerHeight - padding

  const scaleX = availableWidth / ORIGINAL_WIDTH
  const scaleY = availableHeight / ORIGINAL_HEIGHT

  // 取较小的缩放比例，保证内容完整显示
  return Math.min(scaleX, scaleY)
})

// 打开全屏
const openFullscreen = () => {
  isFullscreen.value = true
}

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

// 计算属性 moved to SystemTopology.vue
</script>

<style scoped lang="scss">
.system-diagram {
  // flex: 0;
  min-width: 900px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
}

.diagram-content {
  position: relative;
  width: 900px;
  height: 577px;
  background-image: url('@/assets/images/station-monitor/image_11.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.diagram-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 900px;
  height: 234px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 100%);
  display: flex;
  align-items: flex-start;
  padding: 16px;
  justify-content: space-between;
  pointer-events: none;
  z-index: 10;
}

.diagram-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: 400;
  line-height: normal;
}

.fullscreen-icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-top: 2px;
  pointer-events: auto;
}

// 全屏对话框样式
.fullscreen-dialog {
  :deep(.v-overlay__content) {
    max-width: none !important;
    max-height: none !important;
  }
}

.fullscreen-card {
  border-radius: 30px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  // background-image: url('@/assets/images/station-monitor/dialog-bg.jpg');
  // background-size: 100% 100%;
  // background-repeat: no-repeat;
  background: linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)),
    url('@/assets/images/station-monitor/dialog-bg.jpg') center/cover no-repeat;

}

.fullscreen-header {
  position: relative;
  height: 60px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  z-index: 10;
}

.fullscreen-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
  line-height: normal;
}

.close-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  color: #000; // 确保图标颜色可见

  &:hover {
    transform: scale(1.1);
  }
}

.fullscreen-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  padding-bottom: 28px;
}

.fullscreen-content-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center center;
  border-radius: 20px;
  // background: #fff;
}

.fullscreen-content {
  position: relative;
  width: 900px;
  height: 577px;
  background-image: url('@/assets/images/station-monitor/image_11.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  flex-shrink: 0;
}
</style>
